import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { MOBILE_BREAKPOINT } from "../../config";
import { MediaType } from "../../graphql/generated";
import styled from "../../styles";
import { getCssAspectRatio } from "../../utils/content";
import { MediaItem } from "./types";

interface MediaPlayerProps {
  media: MediaItem;
  aspectRatio: string;
  onTimeUpdate?: (time: number) => void;
}

export type MediaPlayerHandle = {
  seekTo: (time: number) => void;
};

export const MediaPlayer = forwardRef<MediaPlayerHandle, MediaPlayerProps>(
  ({ media, aspectRatio, onTimeUpdate }: MediaPlayerProps, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const isVideo = media.mediaType === MediaType.Video;

    const handleTimeUpdate = useCallback(() => {
      if (!isVideo || !videoRef.current) return;
      const newTime = videoRef.current.currentTime;
      onTimeUpdate?.(newTime);
    }, [isVideo, onTimeUpdate]);

    const seekToTime = useCallback(
      (time: number) => {
        if (!isVideo || !videoRef.current) return;
        videoRef.current.currentTime = time;
        videoRef.current.play();
      },
      [isVideo]
    );

    useImperativeHandle(
      ref,
      () => ({
        seekTo: (time: number) => seekToTime(time),
      }),
      [seekToTime]
    );

    useEffect(() => {
      if (isVideo && videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    }, [media, isVideo]);

    return (
      <MediaSection aspectRatio={getCssAspectRatio(aspectRatio)}>
        {isVideo ? (
          <video
            ref={videoRef}
            controls
            onTimeUpdate={handleTimeUpdate}
            key={media.mediaUrl}
          >
            <source src={media.mediaUrl} type="video/mp4" />
          </video>
        ) : (
          <img
            src={media.mediaUrl}
            alt="Submitted content"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        )}
      </MediaSection>
    );
  }
);

// Removed global seek hook; use imperative ref instead

const MediaSection = styled.div<{ aspectRatio: string }>`
  /* Core: Always black background, center content */
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  /* CRITICAL: Always fill 100% of parent container height */
  height: 100%;

  /* Desktop & Tablet */
  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    ${(p) => {
      const [width, height] = p.aspectRatio.split("/").map(Number);
      const ratio = width / height;

      /* Sidebar needs minimum 440px */
      const sidebarWidth = "440px";
      const containerHeight = "min(85vh, 900px)";

      return `
        /* Calculate ideal width based on height and aspect ratio */
        width: calc(${containerHeight} * ${ratio});
        
        /* But constrain to leave room for sidebar */
        max-width: calc(100vw - ${sidebarWidth} - 4rem);
        
        /* For very narrow content (e.g., 9:16 portrait), ensure minimum width */
        min-width: min(400px, calc(100vw - ${sidebarWidth} - 4rem));
        
        /* Tablet adjustments */
        @media (max-width: 1200px) {
          max-width: calc(100vw - 400px - 3rem);
          min-width: min(350px, calc(100vw - 400px - 3rem));
        }
        
        /* Small tablet */
        @media (max-width: 1024px) {
          max-width: calc(100vw - 380px - 2rem);
          min-width: min(300px, calc(100vw - 380px - 2rem));
        }
      `;
    }}
  }

  /* Mobile: Stack layout */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    /* Always full viewport width on mobile */
    width: 100vw;

    ${(p) => {
      const [width, height] = p.aspectRatio.split("/").map(Number);
      const ratio = width / height;

      /* Different handling based on aspect ratio */
      if (ratio < 0.7) {
        /* Portrait (9:16, etc.) - prioritize height */
        return `
          height: min(70vh, calc(100vw / ${ratio}));
        `;
      } else if (ratio > 1.3) {
        /* Landscape (16:9, etc.) - constrain height more */
        return `
          height: calc(100vw / ${ratio});
          max-height: 40vh;
          min-height: 25vh;
        `;
      } else {
        /* Square-ish (1:1, 4:5, etc.) - balanced */
        return `
          height: calc(100vw / ${ratio});
          max-height: 50vh;
          min-height: 35vh;
        `;
      }
    }}
  }

  /* Media element always fills container with proper scaling */
  video,
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }
`;
